import { Video, ReceiptText, Shield, ShieldCheck, type LucideIcon } from 'lucide-react';

/* The four "talk to us" request forms a signed-in client can send, and the one
   place their copy lives. The client request page (ServiceRequest.tsx) renders a
   kind from here, and the admin queue (AdminServiceRequests.tsx) turns the same
   list into its tabs — so a new kind is one entry in this array, not a new page
   plus a new inbox. `id` is what the backend stores on the request. */

export type RequestKindId = 'consultation' | 'tax-filings' | 'corporate-services' | 'external-audit';

export type RequestKind = {
  id: RequestKindId;
  /** URL segment under /request/ — consultation also keeps its own /book-consultation. */
  slug: string;
  /** Tab label in admin, short. */
  label: string;
  /** Page heading on the client form. */
  title: string;
  /** One line under the heading. */
  blurb: string;
  /** Button label on the client form. */
  cta: string;
  /** What the confirmation screen promises. */
  promise: string;
  icon: LucideIcon;
  /** Accent used for the client form's icon chip and primary button. */
  accent: string;
  topics: string[];
};

export const REQUEST_KINDS: RequestKind[] = [
  {
    id: 'consultation',
    slug: 'consultation',
    label: 'Consultations',
    title: 'Talk to a Conncct advisor',
    blurb: "Tell us what you'd like help with and we'll get back to you to arrange a call. It's free.",
    cta: 'Request consultation',
    promise: 'will reach out to schedule your consultation, usually within one business day',
    icon: Video,
    accent: '#1D74F5',
    topics: [
      'General consultation',
      'Fundraising & investor readiness',
      'Corporate Tax & VAT',
      'Legal & compliance',
      'Financial model / KPIs',
      'Company setup / visas',
      'Other',
    ],
  },
  {
    id: 'tax-filings',
    slug: 'tax-filings',
    label: 'Tax & filings',
    title: 'Tax & Corporate Filings',
    blurb: 'VAT registration & returns, Corporate Tax registration, consultation & submission. Tell us what you need and our tax team will take it from there.',
    cta: 'Request filing support',
    promise: 'will review what you need and come back with the documents required and a timeline',
    icon: ReceiptText,
    accent: '#8A6A00',
    topics: [
      'VAT registration',
      'VAT return filing',
      'Corporate Tax registration',
      'Corporate Tax return / submission',
      'Excise tax',
      'Tax consultation / advisory',
      'Penalty or FTA notice',
      'Other',
    ],
  },
  {
    id: 'corporate-services',
    slug: 'corporate-services',
    label: 'Corporate services',
    title: 'Corporate Services Support',
    blurb: "Company setup, licensing, visas, PRO work and legal housekeeping. Tell us where you are and our team will map out the steps.",
    cta: 'Request support',
    promise: 'will get back to you with the process, documents and costs for what you need',
    icon: Shield,
    accent: '#475569',
    topics: [
      'Company formation / incorporation',
      'Trade licence renewal or amendment',
      'Visas & immigration',
      'PRO / government liaison',
      'Bank account opening',
      'Corporate documents & attestation',
      'goAML / compliance registration',
      'Other',
    ],
  },
  {
    id: 'external-audit',
    slug: 'external-audit',
    label: 'External audit',
    title: 'External Auditing',
    blurb: 'Statutory audits, agreed-upon procedures and audit-ready financials, delivered by a licensed audit partner.',
    cta: 'Request an audit',
    promise: 'will match you with an audit partner and come back with scope, timing and a quote',
    icon: ShieldCheck,
    accent: '#1D74F5',
    topics: [
      'Statutory / annual audit',
      'Audit-readiness review',
      'Agreed-upon procedures',
      'Internal audit',
      'Liquidation audit',
      'Free-zone authority audit report',
      'Other',
    ],
  },
];

export const REQUEST_KIND_BY_ID: Record<string, RequestKind> =
  Object.fromEntries(REQUEST_KINDS.map(k => [k.id, k]));

export const REQUEST_KIND_BY_SLUG: Record<string, RequestKind> =
  Object.fromEntries(REQUEST_KINDS.map(k => [k.slug, k]));

/** Path to a kind's client-facing form. */
export const requestPath = (id: RequestKindId) =>
  id === 'consultation' ? '/book-consultation' : `/request/${REQUEST_KIND_BY_ID[id]?.slug ?? id}`;

export const REQUEST_STATUSES = ['new', 'contacted', 'scheduled', 'done', 'cancelled'] as const;
export type RequestStatus = typeof REQUEST_STATUSES[number];

export const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  scheduled: 'Scheduled',
  done: 'Done',
  cancelled: 'Cancelled',
};

/** Old rows stored `status: 'new'` only; anything unrecognised reads as New. */
export const normStatus = (s?: string): RequestStatus =>
  (REQUEST_STATUSES as readonly string[]).includes(s || '') ? (s as RequestStatus) : 'new';
